import { AxiosResponse } from 'axios';
import { apiClient } from '../../apiHelper';
import {
  useCreate,
  useEdit,
  usePaginated,
  useSingleFetch,
} from '../react-query';
import { userPreferencesQueryKeys, usersQueryKeys } from './users-query-keys';

export interface User {
  id: number;
  uid: string;
  name: string;
  username: string;
  email: string;
  avatar?: string;
  role: string;
  is_active: boolean;
  last_login?: Date;
  acl_roles?: string[];
  is_tenant_owner?: boolean;
}
export enum ThemeEnum {
  LIGHT = 'light',
  DARK = 'dark',
}

export enum LanguageEnum {
  EN = 'en',
  FR = 'fr',
}
export interface Preferences {
  id: number;
  user_id: string; 
  theme: ThemeEnum;
  language: LanguageEnum;
  
}
interface PreferenceUpdate {
  user_id: string;
  theme: ThemeEnum;
  language: LanguageEnum;
}

interface PaginatedResponse<T> {
  data: {
    items: T[];
    total: number;
    page: number;
    items_per_page: number;
  };
}

interface PasswordResetData {
  uid: string;
  form: {
    password: string;
    password_confirm: string;
  };
}

interface UserProfile {
  id: number;
  name: string;
  username: string;
  avatar: string;
  email: string;
  uid: string;
  is_active: boolean;
  last_login: string; // You can use Date type depending on how you want to handle the date
  role?: string;
  roles?: string[];
  is_super_admin?: boolean;
}

export function getProfile(config = {}) {
  const getProfileFn = async (): Promise<UserProfile> => {
    const response = await apiClient.get(`users/getProfile`);
    return response;
  };

  return useSingleFetch(
    getProfileFn,
    usersQueryKeys.detail('profile'),
    'profile',
    config
  );
}
export function useSingleUser(uid: string, config = {}) {
  const getUserFn = async (): Promise<User> => {
    const response = await apiClient.get<User>(`users/${uid}`);
    return response;
  };

  return useSingleFetch(getUserFn, usersQueryKeys.detail(uid), uid, config);
}
export function usePreferenceUser() {
  const getPreferencesFn = async (): Promise<Preferences> => {
    const response = await apiClient.get(`/preferences`);
    return response;
  };

  return useSingleFetch(getPreferencesFn, userPreferencesQueryKeys.detail(), 'preferences');
}
export function usePreferenceUserUpdate(
  onSuccess?: (response: AxiosResponse, data: Preferences) => void,
  onError?: (response: AxiosResponse, error: Error) => void,
  notification: boolean = true
) {
  const updatePreferencesFn = async ({ values }: PreferenceUpdate): Promise<AxiosResponse<Preferences>> => {
    return await apiClient.put('/preferences', values);
  };

  return useEdit(
    updatePreferencesFn,
    userPreferencesQueryKeys.all.map(String), // or detail() if more specific
    'Error while updating preferences',
    'Preferences updated successfully',
    onSuccess,
    onError,
    notification
  );
}
export function useMe(config = {}) {
  const getMeFn = async (): Promise<User> => {
    const response = await apiClient.get<User>(`/users/me`);
    return response?.data;
  };

  return useSingleFetch(getMeFn, usersQueryKeys.detail('me'), 'me', config);
}

// Candidate users for ownership transfer. The /users list is ACL-scoped on the
// backend (the owner sees all users), so no legacy role filter - any active user
// can receive ownership; the caller excludes self + the current owner.
export function useAdminUsers(config = {}) {
  const getAdminsFn = async (): Promise<any[]> => {
    const response = await apiClient.get(`/users?page=1&items_per_page=200`);
    return response?.data?.items ?? response?.data?.data?.items ?? response?.data?.items ?? [];
  };

  return useSingleFetch(getAdminsFn, usersQueryKeys.detail('admins'), 'admins', config);
}

// export function usePaginatedUsers(
//   page: number,
//   itemsPerPage: number,
//   filters: Record<string, string> = {},
//   config = {}
// ) {
//   const getPaginatedUsersFn = async (
//     p: number = page,
//     f: Record<string, string> = filters
//   ): Promise<PaginatedResponse<User>> => {
//     const filtersQuery = Object.keys(f)
//       .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(f[key])}`)
//       .join('&');
//     const url = `users/?page=${p}&items_per_page=${itemsPerPage}${
//       filtersQuery ? `&${filtersQuery}` : ''
//     }`;

//     const response = await apiClient.get<PaginatedResponse<User>>(url);
//     return response.data;
//   };

//   return usePaginated(getPaginatedUsersFn, usersQueryKeys.pagination(page), page, config);
// }

export function usePaginatedUsers(
  page: number,
  itemsPerPage: number,
  filters: Record<string, any> = {},
  config = {}
) {
  const getPaginatedUsersFn = async (
    p: number = page,
    f: Record<string, any> = filters
  ): Promise<PaginatedResponse<User>> => {
    let url = `/users?page=${p}&items_per_page=${itemsPerPage}`;

    // Add filters to URL if they exist
    if (f.filters) {
      url += `&filters=${encodeURIComponent(f.filters)}`;
    }

    const response = await apiClient.get<PaginatedResponse<User>>(url);
    return response.data;
  };

  return usePaginated(
    () => getPaginatedUsersFn(page, filters),
    usersQueryKeys.pagination(page, filters),
    page,
    config
  );
}

export function usePasswordRecovery() {
  const passwordRecoveryFn = async (uid: string): Promise<void> => {
    const response = await apiClient.post(`users/recovery/${uid}`);
    return response;
  };

  return useEdit(
    passwordRecoveryFn,
    usersQueryKeys.all,
    'Error while performing password recovery',
    'Password recovery link sent successfully'
  );
}

export function usePasswordReset() {
  const passwordResetFn = async (data: {
    password: string;
    password_confirm: string;
  }): Promise<void> => {
    const response = await apiClient.post(`users/reset_password`, data);
    return response;
  };

  return useEdit(
    passwordResetFn,
    usersQueryKeys.all,
    'Error while performing password reset',
    'Password reset successfully'
  );
}

export function useUserPasswordReset() {
  const passwordResetFn = async (data: PasswordResetData): Promise<void> => {
    const response = await apiClient.post(
      `users/reset_password/${data.uid}`,
      data.form
    );
    return response;
  };

  return useEdit(
    passwordResetFn,
    usersQueryKeys.all,
    'Error while performing password reset',
    'Password reset successfully'
  );
}

export function useActivateUser(
  config: { onSuccess?: () => void; onError?: () => void } = {}
) {
  const activateUserFn = async (id: string): Promise<void> => {
    const response = await apiClient.put(`users/${id}/activate`);
    return response;
  };

  return useEdit(
    activateUserFn,
    usersQueryKeys.all,
    'users.activateFailed',
    'users.activatedSuccess',
    config.onSuccess,
    config.onError
  );
}

export function useDeactivateUser(
  config: { onSuccess?: () => void; onError?: () => void } = {}
) {
  const deactivateUserFn = async (id: string): Promise<void> => {
    const response = await apiClient.put(`users/${id}/deactivate`);
    return response;
  };

  return useEdit(
    deactivateUserFn,
    usersQueryKeys.all,
    'users.deactivateFailed',
    'users.deactivatedSuccess',
    config.onSuccess,
    config.onError
  );
}

export function useDeleteUser(
  config: { onSuccess?: () => void; onError?: (error?: any) => void } = {}
) {
  const deleteUserFn = async (id: string): Promise<void> => {
    // Assuming the actual API call returns a promise that resolves to void or AxiosResponse
    // If it returns data that useEdit expects to be void, ensure it's transformed or handled.
    const response = await apiClient.delete(`users/${id}`);
    // If apiClient.delete returns an AxiosResponse, and useEdit expects void,
    // you might not need to return response directly, or adjust types.
    // For now, assuming it aligns or is handled by useEdit.
    return response as any; // Using as any to bypass potential AxiosResponse vs void mismatch for now
  };

  return useEdit(
    deleteUserFn,
    usersQueryKeys.all,
    'Error while deleting User',
    'User deleted successfully',
    config.onSuccess,
    config.onError
  );
}

export function useCreateUser() {
  const createUserFn = async (data: Record<string, unknown>) => {
    const response = await apiClient.post(`/users`, data);
    return response;
  };

  return useCreate(
    createUserFn,
    [...usersQueryKeys.all.map(String)],
    'Error while creating user',
    'User created successfully'
  );
}

export function useImportUsersCsv() {
  const importUsersCsvFn = async (data: FormData) => {
    const response = await apiClient.post(`/users/import-csv`, data);
    return response;
  };

  return useCreate(
    importUsersCsvFn,
    [...usersQueryKeys.all.map(String)],
    'Error while importing users',
    'Users imported successfully',
    undefined,
    undefined,
    false
  );
}

export function useVerifyAccountSetup(
    onSuccess?: (response: AxiosResponse, data: any) => void,
    onError?: (responce: AxiosResponse, error: Error) => void,
) {
  const verifyAccountSetupFn = async (data: {
    token: string;
    password: string;
    last_name: string;
    first_name: string;
    phone_number: string;
  }): Promise<void> => {
    const response = await apiClient.post(`/users/verify-account-setup`, data);
    return response;
  };

  return useEdit(
    verifyAccountSetupFn,
    usersQueryKeys.all,
    'Error while verifying account setup',
    'Account setup verified successfully',
    onSuccess,
    onError
  );
}

export function useVerifyCode(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void
) {
  const verifyCodeFn = async (data: { email: string; code: string }) => {
    const response = await apiClient.post(`/users/verify-code`, data);
    return response;
  };
  return useEdit(
    verifyCodeFn,
    usersQueryKeys.all,
    'Invalid or expired code',
    'Code verified',
    onSuccess,
    onError
  );
}

export function useResendCode(
  onSuccess?: (response: AxiosResponse, data: any) => void,
  onError?: (response: AxiosResponse, error: Error) => void
) {
  const resendCodeFn = async (data: { email: string }) => {
    const response = await apiClient.post(`/users/resend-code`, data);
    return response;
  };
  return useEdit(
    resendCodeFn,
    usersQueryKeys.all,
    'Could not resend the code',
    'A new code was sent',
    onSuccess,
    onError
  );
}

export function useForgotPassword(
  config: { onSuccess?: () => void; onError?: () => void } = {}
) {
  const forgotPasswordFn = async (data: { email: string }): Promise<void> => {
    const response = await apiClient.post(`/forgot-password`, data);
    return response;
  };

  return useEdit(
    forgotPasswordFn,
    usersQueryKeys.all,
    'Error while processing forgot password request',
    'Password reset email sent successfully',
    config.onSuccess,
    config.onError
  );
}

export function useResetPassword(
  config: { onSuccess?: () => void; onError?: (error: any) => void } = {}
) {
  const resetPasswordFn = async (data: {
    code: string;
    email: string;
    new_password: string;
  }): Promise<void> => {
    const response = await apiClient.post(`/reset-password`, data);
    return response;
  };

  return useEdit(
    resetPasswordFn,
    usersQueryKeys.all,
    'Error while resetting password',
    'Password reset successfully',
    config.onSuccess,
    config.onError,
    false
  );
}

export function useResendVerification(
  onSuccess?: (response: any, data: any) => void,
  onError?: (response: any, error: Error) => void
) {
  const resendVerificationFn = async (
    arg: string | { userId: string; channel?: string }
  ): Promise<void> => {
    try {
      const userId = typeof arg === 'string' ? arg : arg.userId;
      const channel = typeof arg === 'string' ? undefined : arg.channel;
      const q = channel ? `?channel=${channel}` : '';
      const response = await apiClient.post(
        `/users/${userId}/resend-verification${q}`
      );
      return response;
    } catch (error) {
      console.error("Error in resendVerificationFn:", error);
      throw error;
    }
  };

  return useEdit(
    resendVerificationFn,
    usersQueryKeys.all,
    'Error while resending verification email',
    'Verification email sent successfully',
    onSuccess,
    onError
  );
}

export function useAdminSendPasswordReset(
  onSuccess?: (response: any, data: any) => void,
  onError?: (response: any, error: Error) => void
) {
  const sendPasswordResetFn = async (
    arg: string | { userId: string; channel?: string }
  ): Promise<void> => {
    try {
      const userId = typeof arg === 'string' ? arg : arg.userId;
      const channel = typeof arg === 'string' ? undefined : arg.channel;
      const q = channel ? `?channel=${channel}` : '';
      const response = await apiClient.post(`/users/${userId}/password-reset${q}`);
      return response;
    } catch (error) {
      console.error("Error in sendPasswordResetFn:", error);
      throw error;
    }
  };

  return useEdit(
    sendPasswordResetFn,
    usersQueryKeys.all,
    'Error while sending password reset email',
    'Password reset email sent successfully',
    onSuccess,
    onError
  );
}

export function useAdminSetTemporaryPassword(
  onSuccess?: (response: any, data: any) => void,
  onError?: (response: any, error: Error) => void
) {
  const setTemporaryPasswordFn = async (data: {
    userId: string;
    temporary_password: string;
  }): Promise<void> => {
    try {
      const response = await apiClient.post(
        `/users/${data.userId}/temporary-password`,
        { temporary_password: data.temporary_password }
      );
      return response;
    } catch (error) {
      console.error("Error in setTemporaryPasswordFn:", error);
      throw error;
    }
  };

  return useEdit(
    setTemporaryPasswordFn,
    usersQueryKeys.all,
    'Error while setting temporary password',
    'Temporary password set successfully',
    onSuccess,
    onError
  );
}



export function useAdress(subdomain: string) {
  const testFn = async (): Promise<any> => {
    const response = await apiClient.get(`/pacs_address?subdomain=${encodeURIComponent(subdomain)}`);
    return response;
  };

  return useSingleFetch(testFn, usersQueryKeys.all, 'test', {});
}
